import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * A page's title line (z8nrz7e46x): the title, its icon when it has one, and
 * the description — all on ONE line, in that order, with a thin divider before
 * the description. On a narrow screen the description wraps underneath rather
 * than squeezing the title.
 *
 * ## Alignment
 *
 * The row aligns on the text BASELINE, not the box centre. A 24px title and a
 * 14px description centred on each other sit with their bottoms at different
 * heights, and a description that wraps to two lines would centre against the
 * whole block and float away from the title. On the baseline, the first line of
 * the description always reads as a continuation of the title.
 *
 * Title and icon share one group: the group is centred internally (the icon is
 * a button with no text of its own) and takes its baseline from the `<h1>`,
 * which is what the description lines up against. The divider has no text, so
 * its baseline is its bottom edge; it is nudged down by the description's
 * descender so it spans the description's letters rather than sitting above them.
 *
 * Only the title is an `<h1>`: the icon and description sit outside it so
 * heading locators keep matching the title alone.
 */
export function PageHeading({
  title,
  description,
  icon,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  /** Rendered right after the title, before the description — e.g. an OverallStatusLink. */
  icon?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1', className)}>
      <div className='flex items-center gap-3'>
        <h1 className='text-2xl font-semibold leading-8 tracking-tight'>{title}</h1>
        {icon}
      </div>
      {description && (
        <>
          <span aria-hidden className='hidden h-4 w-px translate-y-[4px] bg-border sm:block' />
          {/* Takes the rest of the line and wraps within it; drops below the
              title only when less than 16rem is left beside it. */}
          <p className='min-w-[16rem] flex-1 text-sm leading-5 text-muted-foreground'>{description}</p>
        </>
      )}
    </div>
  )
}
